import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { Trans } from '@common/i18n/trans';
import { useFormContext } from 'react-hook-form';
import { Button } from '@common/ui/buttons/button';
import { AddIcon } from '@common/icons/material/Add';
import { List, ListItem } from '@common/ui/list/list';
import { RateSheetPayload } from '../requests/update-rate-sheet-settings';
import { RateSheetItem } from './rate-sheet-item';
import { useSettings } from '@common/core/settings/use-settings';
import { ParentRowSheet } from './row-sheet-parent';
import { FileItem } from './file-item';
import { RowSheet } from './rate-sheet-row';

export function CrupdateRateSheetForm({
  id,
  market_rate_date,
  data_items,
  timeChange,
}: {
  id: number;
  market_rate_date: string;
  data_items?: any;
  timeChange: any;
}) {
  const form = useFormContext<RateSheetPayload>();
  const [items, setItems] = useState<RateSheetItem[]>(data_items.length ? data_items : [{ key: "1" }]);
  const len = data_items ? data_items.length : 1;
  const [cnt, setCnt] = useState(len);
  const [last, setLast] = useState(len);

  // State to track selected sheet names for each lender (pidx)
  const [selectedSheetNamesMap, setSelectedSheetNamesMap] = useState<{ [key: number]: string[] }>({});

  const handleSelectedSheetNamesChange = (pidx: number, selectedSheetNames: string[]) => {
    setSelectedSheetNamesMap((prev) => ({
      ...prev,
      [pidx]: selectedSheetNames,
    }));
  };

  const [selectedLoanProgramTypesMap, setSelectedLoanProgramTypesMap] = useState<{
    [key: number]: { sheetName: string; types: string[] }[];
  }>({});

  const handleSelectedLoanProgramTypesChange = (
    pidx: number,
    selectedLoanProgramTypes: { sheetName: string; types: string[] }[]
  ) => {
    setSelectedLoanProgramTypesMap((prev) => ({
      ...prev,
      [pidx]: selectedLoanProgramTypes,
    }));

    // Update the item's sheet_names with the selected loan_program_types
    setItems((prevItems) => {
      const updatedItems = [...prevItems];
      const item = updatedItems[pidx];
      if (item && item.sheet_names) {
        item.sheet_names = Object.keys(item.sheet_names).map((sheet_name: any) => {
          const sheet = item.sheet_names?item.sheet_names[sheet_name]:[];
          const selection = selectedLoanProgramTypes.find(sel => sel.sheetName === sheet_name);
          return {
            ...sheet,
            loan_program_type: selection ? selection.types : sheet.loan_program_type || [],
          };
        });
      }
      return updatedItems;
    });
  };

  useEffect(() => {
    setItems(items);
    setLast(last + 1);
  }, [cnt]);

  const [subItems, setSubItems] = useState([]);
  const onAddSubItems = useCallback((pidx: number, itemSubItems: any) => {
    items[pidx]['subItems'] = itemSubItems;
    setSubItems(itemSubItems);
  }, [items]);

  const removeParent = (canRemove: boolean, pidx: number) => {
    if (canRemove) {
      items.splice(pidx, 1);
      setCnt(cnt - 1);
    }
  };

  const { market_rate } = useSettings();
  const loan_program_types = market_rate?.loan_program_types ? JSON.parse(market_rate.loan_program_types) : [];
  const investor_codes = market_rate?.investor_codes ? JSON.parse(market_rate.investor_codes) : [];

  useEffect(() => {
    setItems(data_items);
  }, [data_items, timeChange]);

  useEffect(() => {
    setItems(items);
  }, [subItems]);

  const [uploadedFileItems, setUploadedFileItems] = useState([]);

  useEffect(() => {
    setSubItems(subItems);
    setLast(last + 1);
  }, [cnt]);

  useEffect(() => {
    if (subItems) {
      setSubItems(subItems);
    }
  }, [subItems]);

  const [investorCode, setInvestorCode] = useState('');

  const changeInvestorCode = (investorCode: string) => {
    setInvestorCode(investorCode);
  };

  const [fileItems, setFileItems] = useState<FileItem[]>([]);
  const changeFileItems = (fileItems: any) => {
    setFileItems(fileItems);
  };

  return (
    <Fragment>
      <div className="gap-10">
        <div className="flex">
          <div className="w-[10%] p-10">
            <Button
              startIcon={<AddIcon />}
              variant="flat"
              color="primary"
              onClick={() => {
                items.push({ key: last.toString(), subItems: [{ key: "1", loan_program_type: "", investor_code: "", prompt: "" }] });
                setCnt(cnt + 1);
              }}
            >
              <Trans message="Add Lender" />
            </Button>
          </div>
        </div>

        <aside className="sticky top-10 w-[100%] flex">
          <List padding="p-0" className="scroll-p-8 overflow-y-scroll min-h-600 w-[900%]">
            {items?.map((item: any, pidx: number) => (
              <ListItem className="border-b-1" key={`meta-${pidx}`}>
                <>
                  <ParentRowSheet
                    key={`fist-row-${pidx}-${id}`}
                    id={id}
                    market_rate_date={market_rate_date}
                    pidx={pidx}
                    item={item}
                    subItems={subItems}
                    onAddSubItems={onAddSubItems}
                    investor_codes={investor_codes}
                    cnt={cnt}
                    setCnt={setCnt}
                    removeParent={removeParent}
                    last={last}
                    changeInvestorCode={changeInvestorCode}
                    fileItems={fileItems}
                    sheetNames={selectedSheetNamesMap[pidx]}
                    loanProgramTypes={selectedLoanProgramTypesMap[pidx]}
                  />
                  
                  <RowSheet
                    subItems={subItems}
                    key={`row-${pidx}-${id}`}
                    id={id}
                    market_rate_date={market_rate_date}
                    investor_code={investorCode}
                    item={item}
                    pidx={pidx}
                    uploadedFileItems={uploadedFileItems}
                    onAddSubItems={onAddSubItems}
                    setCnt={setCnt}
                    cnt={cnt}
                    loan_program_types={loan_program_types}
                    changeFileItems={changeFileItems}
                    onSelectedSheetNamesChange={handleSelectedSheetNamesChange}
                    onSelectedLoanProgramTypesChange={handleSelectedLoanProgramTypesChange}
                  />
                </>
              </ListItem>
            ))}
          </List>
        </aside>
      </div>
    </Fragment>
  );
}